import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {UserSubscription} from "../../../../../user/user-subscription";
import tabs from "./io-tabs.json";
import {Website} from "../../../website";
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {TypeUtils} from "../../../../../shared/utils/type-utils";
import {WebsiteService} from "../../../../website.service";
import {Subject} from "rxjs";
import {IOPagesData} from "../../mappers/io-list";
import {GoogleAnalyticsService} from "../../../../../shared/services/google-analytics/google-analytics.service";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../../store";
import {IBoosterStats} from "../../../../../store/reducers/booster.reducer";
import {ActivityAction, ActivityStatus} from "../../../../../activities/activity";
import {ActivatedRoute} from "@angular/router";
import {Observable} from "rxjs";
import {DialogService} from "../../../../../../ng2-bootstrap-modal/dialog.service";
import {DbModalService} from "../../../../../modules/modal/modal";
import {PerformanceService} from "../../../performance/performance.service";


interface ITab {
    id: 'media' | 'pages' | string;
    label: string;
}

@Component({
    selector: 'db-image-optimizer-section',
    templateUrl: './image-optimizer-section.component.html',
    styleUrls: ['./image-optimizer-section.component.scss'],
    standalone: false
})
export class ImageOptimizerSectionComponent implements OnInit, OnChanges, OnDestroy {

    @Input() website: Website;
    @Input() currentSubscription: UserSubscription;
    @Input() boosterPluginIsActive: boolean;
    @Input() websiteScore: any;

    public pageLayout: boolean = false;
    public tabs: ITab[] = tabs;
    public activeTab: ITab['id'] = tabs[0].id;
    public optimizedPages: IOPagesData[];
    public optimizedImages: IOPagesData[];
    public optimizedPagesLoading: boolean = true;
    public loading: boolean;
    public inProgress: boolean = false;
    public stats: IBoosterStats;
    public optimizerDisabled: boolean;
    public error: boolean = false;
    public optimizeImagesStats$: Observable<any>;

    public currentScore: any;

    private _destroyStream$: Subject<boolean> = new Subject<boolean>();

    constructor(
        private _websiteService: WebsiteService,
        private _store: Store<fromStore.DbStoreState>,
        private _googleAnalyticsService: GoogleAnalyticsService,
        private _route: ActivatedRoute,
        private _dialogService: DialogService,
        private _dbModal: DbModalService,
        private _performanceService: PerformanceService,
    ) {
    }

    ngOnInit() {
        this.pageLayout = this._route.snapshot.data['pageId'] === 'image-optimizer';
        this.checkScore();
        this._addEventHandlers();
        this.getReportData();
    }

    public setActiveTab(tabId: string): void {
        this.activeTab = tabId as ITab['id'];
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['websiteScore'] && changes['websiteScore'].currentValue) {
            this.websiteScore = changes['websiteScore'].currentValue;
            this._getScoreInfo(this.websiteScore && this.websiteScore.currentScore);
        }
    }

    private _getScoreInfo(currentScore): void {
        if (currentScore) {
            this.currentScore = currentScore;
        }
    }

    public checkScore(): void {
        if (TypeUtils.isNull(this.websiteScore) || !this.websiteScore.currentScore || this.websiteScore.currentScore.desktopScore === 101) {
            this._performanceService.getGooglePageSpeedScore(this.website?.id);
        }
    }

    public getReportData(updateCache = false): void {
        this._websiteService.getImageOptimizerPagesMediaReport(this.website?.id, updateCache)
            .pipe(
                first(response => !!response)
            )
            .subscribe((response): void => {
                if (!TypeUtils.isNull(response)) {
                    this.optimizedPagesLoading = false;
                    this.optimizedPages = response.data.pagesData;
                    this.optimizedImages = response.data.bulkData;
                }
            });
    }

    public contactUs(): void {
        this.redirectToWPSupport();
    }

    ngOnDestroy() {
        this._destroyStream$.next(true);
        this._destroyStream$.complete();
    }

    private _addEventHandlers(): void {
        this._store.select(fromStore.getCurrentWorkspaceId)
            .pipe(
                takeUntil(this._destroyStream$),
                filter(workspaceId => !!workspaceId), // Only proceed if there's a valid workspace ID
                switchMap(workspaceId =>
                    this._store.select(fromStore.getOngoingActivities)
                )
            )
            .subscribe((data: any) => {
                const optimizerActivity = data && data.length && data.find(action =>
                    [ActivityAction.OPTIMIZE].includes(<any>action.action) && action && action.websiteId === this.website?.id);

                const pageOptimizerActivity = data && data.length && data.find(action =>
                    [ActivityAction.IO_OPTIMIZING_CUSTOM_PAGE].includes(<any>action.action) && action && action.websiteId === this.website?.id);

                if (!TypeUtils.isEmpty(optimizerActivity)) {
                    switch (optimizerActivity.status) {
                        case ActivityStatus.RUNNING:
                            this.loading = true;
                            break;
                        case ActivityStatus.DONE:
                        case ActivityStatus.FAILED:
                        case ActivityStatus.CANCELED:
                            this.loading = false;
                            this._store.dispatch(new fromStore.LoadImageOptimizer({websiteId: this.website?.id}));
                            this.getReportData();
                            break;
                    }
                }

                if (!TypeUtils.isEmpty(pageOptimizerActivity)) {
                    switch (pageOptimizerActivity.status) {
                        case ActivityStatus.RUNNING:
                            this.inProgress = true;
                            break;
                        case ActivityStatus.DONE:
                        case ActivityStatus.FAILED:
                        case ActivityStatus.CANCELED:
                            this.inProgress = false;
                            this._store.dispatch(new fromStore.LoadImageOptimizer({websiteId: this.website?.id}));
                            this.getReportData();
                            break;
                    }
                }
            });

        if (this.pageLayout) {
            this._getImageOptimizer();
        } else {
            this._getWebsiteBoosterStats();
        }
    }

    private _getImageOptimizer(): void {
        this._store.select(fromStore.getWebsiteImageOptimizerPlugin(this.website?.id))
            .pipe(takeUntil(this._destroyStream$))
            .subscribe((imageOptimizerPlugin): void => {
                if (imageOptimizerPlugin) {
                    this.optimizerDisabled = !imageOptimizerPlugin
                        || (imageOptimizerPlugin && (!imageOptimizerPlugin.paidVersionInstalled || !imageOptimizerPlugin.active));
                    if (!this.optimizerDisabled) {
                        this.loading = true;
                        this._getOptimizerStats();
                    }
                }
            });
    }

    private _getOptimizerStats(): void {
        this._store.select(fromStore.getImageOptimizerStats(this.website?.id))
            .pipe(
                takeUntil(this._destroyStream$)
            )
            .subscribe((stats): void => {
                this.loading = false;
                if (!TypeUtils.isNull(stats)) {
                    this.stats = stats;
                    this.loading = this.stats.compressIsRunning || false;
                    this._store.dispatch(new fromStore.SetBoosterStats({
                        websiteId: this.website?.id,
                        stats: stats
                    }));
                }
            });
    }

    private _getWebsiteBoosterStats(): void {
        this.optimizeImagesStats$ = this._store.select(fromStore.getImageOptimizerStats(this.website?.id))
            .pipe(
                takeUntil(this._destroyStream$)
            );

        this.optimizeImagesStats$
            .subscribe(stats => {
                if (!TypeUtils.isNull(stats)) {
                    this.stats = stats;
                    this.error = false;
                    this.loading = this.stats.compressIsRunning || false;
                } else {
                    this.loading = false;
                    this.error = true;
                }
            });
    }

    private redirectToWPSupport(): void {
        window.open('https://help.10web.io/hc/en-us/articles/11304242801810', '_blank', "noopener");
    }

    private openChat(): void {
        window['Smooch']?.open();
        this._store.dispatch(new fromStore.ToggleChatOverlay(false));
    }

}
